/** A document owns one feed. Popup/PiP inherits its scope. Authentication
 * and venue accounts deliberately remain outside this namespace. */
export type FeedKind = 'main' | 'feed7' | 'feed6' | 'transfers' | 'ru' | 'predict' | 'tests' | 'feed3' | 'cupis';

export function feedKindOf(address: { pathname: string; search?: string }): FeedKind {
  const path = address.pathname.replace(/\/+$/, '');
  if (path === '/feed7' || path.startsWith('/feed7/')) return 'feed7';
  if (path === '/feed6' || path.startsWith('/feed6/')) return 'feed6';
  if (path === '/cupis' || path.startsWith('/cupis/')) return 'cupis';
  if (path === '/tests' || path.startsWith('/tests/')) return 'tests';
  if (path === '/feed3' || path.startsWith('/feed3/')) return 'feed3';
  if (/(?:^|\/)transfers$/.test(path)) return 'transfers';
  if (/(?:^|\/)russkie(?:\/(?:live|prematch))?$/.test(path)) return 'ru';
  if (/(?:^|\/)predict$/.test(path)) return 'predict';
  if (/(?:^|\/)studio$/.test(path)) {
    const feed = new URLSearchParams(address.search).get('feed');
    if (feed === 'transfers') return 'transfers';
    if (feed === 'ru') return 'ru';
    if (feed === 'predict') return 'predict';
    if (feed === 'tests') return 'tests';
    if (feed === 'feed3') return 'feed3';
    if (feed === 'feed7') return 'feed7';
    if (feed === 'feed6') return 'feed6';
    if (feed === 'cupis') return 'cupis';
  }
  return 'main';
}

export let FEED_KIND: FeedKind = typeof location === 'undefined' ? 'main' : feedKindOf(location);
export let TRANSFERS = FEED_KIND === 'transfers';
export let RU_FEED = FEED_KIND === 'ru';
/** Polymarket × Predict.fun: свой контейнер forq-predict, без сита pair_allowed. */
export let PREDICT_FEED = FEED_KIND === 'predict';
export let PRIVATE_MIRROR = FEED_KIND === 'cupis';
export let EXPERIMENTS = FEED_KIND === 'tests' || PRIVATE_MIRROR;
export let FEED7 = FEED_KIND === 'feed7';
export let POLYQUANT = FEED_KIND === 'feed6';
export let FEED3 = FEED_KIND === 'feed3';

/** Сменить источник без перезагрузки. Флаги читаются в момент вызова, не при импорте. */
export function adoptFeed(kind: FeedKind): void {
  if (PRIVATE_MIRROR && kind !== 'cupis') return;
  FEED_KIND = kind;
  TRANSFERS = kind === 'transfers';
  RU_FEED = kind === 'ru';
  PREDICT_FEED = kind === 'predict';
  PRIVATE_MIRROR = kind === 'cupis';
  EXPERIMENTS = kind === 'tests' || PRIVATE_MIRROR;
  FEED7 = kind === 'feed7';
  POLYQUANT = kind === 'feed6';
  FEED3 = kind === 'feed3';
}

/**
 * Rows prepared by the BetBurger/feed2 pipeline. Internal adapter/mirror
 * sieves (pair_allowed, hideBooks, twin collapse) stay off. The person's
 * filter-tab and studio settings apply like any other source.
 */
export function isPreparedFeed2Id(id: string): boolean {
  return /^(?:cupis:)?exp:(?:live|prematch):/.test(id);
}

export function isCupisForkId(id: string): boolean {
  return /^cupis:exp:(?:live|prematch):/.test(id);
}

/** Only the destination bypasses its sieve; /tests keeps its own behaviour. */
export function isMainFeed2Id(id: string): boolean {
  return FEED_KIND === 'main' && isPreparedFeed2Id(id);
}

/** Keep existing main keys unchanged: old tabs must keep working. */
export function feedKey(key: string, kind: FeedKind = FEED_KIND,
  pathname = typeof location === 'undefined' ? '' : location.pathname): string {
  if (kind === 'main') return key;
  if (kind === 'cupis') return `forq.cupis.${key}`;
  if (kind === 'tests') return `forq.tests.${key}`;
  if (kind === 'feed7') return `forq.feed7.${key}`;
  if (kind === 'feed6') return `forq.feed6.${key}`;
  if (kind === 'feed3') return `forq.feed3.${key}`;
  const scoped = kind === 'ru' ? 'ru' : kind === 'predict' ? 'predict' : 'transfers';
  return /^\/v2(?:\/|$)/.test(pathname) ? `forq.v2.${scoped}.${key}` : `forq.${scoped}.${key}`;
}

export function feedPrefix(kind: FeedKind = FEED_KIND): string {
  if (kind === 'main') return '';
  if (kind === 'cupis') return '/cupis-api';
  if (kind === 'predict') return '/predict-api';
  if (kind === 'tests') return '/tests-api';
  if (kind === 'feed7') return '/feed7-api';
  if (kind === 'feed6') return '/feed6-api';
  if (kind === 'feed3') return '/feed3-api';
  return kind === 'ru' ? '/russkie-api' : '/transfers-api';
}

/** Какая лента у адреса. /report и витрина — пусто, не угадываем. */
export type Tape = 'feed7' | 'feed6' | 'app' | 'tests' | 'feed3' | 'ru' | 'transfers' | 'predict' | 'hand' | 'cupis' | '';

export function tapeOf(pathname: string): Tape {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (path === '/feed7' || path.startsWith('/feed7/')) return 'feed7';
  if (path === '/feed6' || path.startsWith('/feed6/')) return 'feed6';
  if (path === '/cupis' || path.startsWith('/cupis/')) return 'cupis';
  if (path === '/tests' || path.startsWith('/tests/')) return 'tests';
  if (path === '/feed3' || path.startsWith('/feed3/')) return 'feed3';
  if (/(?:^|\/)russkie(?:\/(?:live|prematch))?$/.test(path)) return 'ru';
  if (/(?:^|\/)transfers$/.test(path)) return 'transfers';
  if (/(?:^|\/)predict$/.test(path)) return 'predict';
  if (path === '/app' || path.startsWith('/app/') || path === '/labs/app' || path.startsWith('/labs/app/')) {
    return 'app';
  }
  return '';
}
